import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFDownloadLink,
  Image,
} from "@react-pdf/renderer";
import { Download, FileText, CheckCircle2 } from "lucide-react";
import logo from "../assets/logo.png";

// 1. Define Professional PDF Styles
const styles = StyleSheet.create({
  page: { padding: 40, backgroundColor: "#FFFFFF", fontFamily: "Helvetica" },
  
  // Top Accent Strip
  topStrip: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 8,
    backgroundColor: "#e11d48", // Rose-600
  },

  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 40,
    marginTop: 10,
  },
  
  companyInfo: {
    flexDirection: "column",
  },
  companyName: {
    fontSize: 16,
    fontFamily: "Helvetica-Bold",
    color: "#1e1b4b",
  },
  companySub: {
    fontSize: 9,
    color: "#64748b",
    marginTop: 2,
  },

  invoiceTitleSection: {
    textAlign: "right",
  },
  invoiceTitle: {
    fontSize: 24,
    fontFamily: "Helvetica-Bold",
    color: "#1e1b4b",
    letterSpacing: 1,
  },
  invoiceId: {
    fontSize: 10,
    color: "#e11d48",
    fontFamily: "Helvetica-Bold",
    marginTop: 4,
  },

  // Details Section
  detailsRow: {
    flexDirection: "row",
    marginBottom: 30,
    borderBottomWidth: 1,
    borderBottomColor: "#f1f5f9",
    paddingBottom: 20,
  },
  infoColumn: {
    flex: 1,
  },
  sectionTitle: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#94a3b8",
    textTransform: "uppercase",
    marginBottom: 6,
    letterSpacing: 1,
  },
  infoText: {
    fontSize: 10,
    color: "#334155",
    lineHeight: 1.4,
  },
  infoBold: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    color: "#1e1b4b",
    marginBottom: 2,
  },

  // Table
  table: { marginTop: 10 },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f8fafc",
    borderBottomWidth: 1,
    borderBottomColor: "#e2e8f0",
    padding: 8,
    alignItems: "center",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#f1f5f9",
    padding: 10,
    alignItems: "center",
  },
  colDesc: { flex: 3 },
  colQty: { flex: 0.5, textAlign: "center" },
  colPrice: { flex: 1, textAlign: "right" },
  colTotal: { flex: 1, textAlign: "right" },

  itemTitle: { fontSize: 10, fontFamily: "Helvetica-Bold", color: "#1e1b4b" },
  itemSub: { fontSize: 8, color: "#64748b", marginTop: 2 },

  // Summary
  summarySection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 20,
  },
  notesArea: {
    width: "50%",
  },
  totalsArea: {
    width: "40%",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
  },
  totalLabel: { fontSize: 10, color: "#64748b" },
  totalValue: { fontSize: 10, fontFamily: "Helvetica-Bold", color: "#1e1b4b" },
  
  grandTotalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 10,
    paddingTop: 10,
    borderTopWidth: 2,
    borderTopColor: "#e11d48",
  },
  grandTotalLabel: { fontSize: 12, fontFamily: "Helvetica-Bold", color: "#1e1b4b" },
  grandTotalValue: { fontSize: 14, fontFamily: "Helvetica-Bold", color: "#e11d48" },

  footer: {
    position: "absolute",
    bottom: 40,
    left: 40,
    right: 40,
    textAlign: "center",
    borderTopWidth: 1,
    borderTopColor: "#f1f5f9",
    paddingTop: 20,
  },
  footerMain: { fontSize: 10, fontFamily: "Helvetica-Bold", color: "#1e1b4b" },
  footerSub: { fontSize: 8, color: "#94a3b8", marginTop: 4 },
});

// 2. The Refined PDF Document Template
const MyInvoicePDF = ({ order }) => {
  const subtotal = parseFloat(order.order_amount || 0);
  const delivery = parseFloat(order.delivery_charges || 0);
  const gst = 0.00; // As requested
  const total = parseFloat(order.total_amount || 0);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.topStrip} />

        {/* Header */}
        <View style={styles.header}>
          <View style={styles.companyInfo}>
            <Image src={logo} style={{ width: 80, marginBottom: 10 }} />
            <Text style={styles.companyName}>FarmFit AgriHealth LLP</Text>
            <Text style={styles.companySub}>Quality Agri-Health Solutions</Text>
          </View>
          <View style={styles.invoiceTitleSection}>
            <Text style={styles.invoiceTitle}>INVOICE</Text>
            <Text style={styles.invoiceId}>NO: {order.order_code}</Text>
          </View>
        </View>

        {/* Billing Details */}
        <View style={styles.detailsRow}>
          <View style={styles.infoColumn}>
            <Text style={styles.sectionTitle}>Billed To</Text>
            <Text style={styles.infoBold}>{order.user_name}</Text>
            <Text style={styles.infoText}>{order.order_address}</Text>
            <Text style={styles.infoText}>Ph: {order.mobile_number || "N/A"}</Text>
          </View>
          <View style={[styles.infoColumn, { textAlign: "right" }]}>
            <Text style={styles.sectionTitle}>Invoice Details</Text>
            <Text style={styles.infoText}>Date: {new Date(order.order_date_time).toLocaleDateString('en-IN')}</Text>
            <Text style={styles.infoText}>Method: {order.payment_mode}</Text>
            <Text style={[styles.infoText, { color: "#10b981", fontFamily: "Helvetica-Bold" }]}>Status: PAID</Text>
          </View>
        </View>

        {/* Product Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.sectionTitle, styles.colDesc]}>Product Description</Text>
            <Text style={[styles.sectionTitle, styles.colQty]}>Qty</Text>
            <Text style={[styles.sectionTitle, styles.colPrice]}>Rate</Text>
            <Text style={[styles.sectionTitle, styles.colTotal]}>Amount</Text>
          </View>

          {order.order_items.map((item, idx) => (
            <View key={idx} style={styles.tableRow}>
              <View style={styles.colDesc}>
                <Text style={styles.itemTitle}>{item.product_name}</Text>
                {item.pack_size && (
                  <Text style={styles.itemSub}>Pack Size: {item.pack_size}</Text>
                )}
              </View>
              <Text style={[styles.infoText, styles.colQty]}>{item.quantity}</Text>
              <Text style={[styles.infoText, styles.colPrice]}>₹{parseFloat(item.price).toFixed(2)}</Text>
              <Text style={[styles.infoBold, styles.colTotal]}>
                ₹{(parseFloat(item.price) * item.quantity).toFixed(2)}
              </Text>
            </View>
          ))}
        </View>

        {/* Calculation Section */}
        <View style={styles.summarySection}>
          <View style={styles.notesArea}>
            <Text style={styles.sectionTitle}>Important Notes</Text>
            <Text style={styles.infoText}>1. This is a digital invoice for your purchase.</Text>
            <Text style={styles.infoText}>2. GST has been calculated at 0% for this transaction.</Text>
          </View>

          <View style={styles.totalsArea}>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Subtotal</Text>
              <Text style={styles.totalValue}>₹{subtotal.toFixed(2)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>GST (0%)</Text>
              <Text style={styles.totalValue}>₹{gst.toFixed(2)}</Text>
            </View>
            <View style={styles.totalRow}>
              <Text style={styles.totalLabel}>Delivery</Text>
              <Text style={[styles.totalValue, delivery === 0 && { color: "#10b981" }]}>
                {delivery > 0 ? `₹${delivery.toFixed(2)}` : "FREE"}
              </Text>
            </View>
            <View style={styles.grandTotalRow}>
              <Text style={styles.grandTotalLabel}>Grand Total</Text>
              <Text style={styles.grandTotalValue}>₹{total.toFixed(2)}</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text style={styles.footerMain}>Thank you for your business!</Text>
          <Text style={styles.footerSub}>FarmFit AgriHealth LLP | Digital Document | No Signature Required</Text>
        </View>
      </Page>
    </Document>
  );
};

// 3. The Refined Modal UI Component
const InvoiceModal = ({ order, isOpen, onClose }) => {
  if (!isOpen || !order) return null;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-slate-900/60 backdrop-blur-md p-4">
      <div className="bg-white w-full max-w-lg rounded-[2.5rem] shadow-2xl border border-slate-100 overflow-hidden animate-in fade-in zoom-in duration-300">
        
        {/* Header Visual */}
        <div className="relative h-32 bg-rose-600 flex items-center justify-center">
            <div className="absolute top-0 left-0 w-full h-full opacity-10">
                <svg width="100%" height="100%"><pattern id="pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse"><circle cx="2" cy="2" r="1" fill="white"/></pattern><rect width="100%" height="100%" fill="url(#pattern)"/></svg>
            </div>
            <div className="bg-white/20 p-4 rounded-3xl backdrop-blur-xl border border-white/30">
                <FileText className="text-white" size={40} />
            </div>
        </div>

        <div className="p-8 text-center -mt-6">
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-50 relative z-10">
                <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Generate Invoice</h2>
                <p className="text-slate-500 text-sm mt-1">FarmFit AgriHealth LLP</p>
                
                <div className="mt-6 space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-dashed border-slate-100">
                        <span className="text-xs font-bold text-slate-400 uppercase">Order ID</span>
                        <span className="text-sm font-black text-slate-700">#{order.order_code}</span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                        <span className="text-xs font-bold text-slate-400 uppercase">Total Paid</span>
                        <span className="text-lg font-black text-rose-600">₹{parseFloat(order.total_amount).toFixed(2)}</span>
                    </div>
                </div>
            </div>

            <div className="flex gap-4 mt-8">
                <button
                    onClick={onClose}
                    className="flex-1 py-4 rounded-2xl font-bold text-sm text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all"
                >
                    Maybe Later
                </button>

                <PDFDownloadLink
                    document={<MyInvoicePDF order={order} />}
                    fileName={`Invoice_${order.order_code}.pdf`}
                    className="flex-1"
                >
                    {({ loading }) => (
                        <button
                            disabled={loading}
                            className={`w-full py-4 rounded-2xl font-black text-sm uppercase tracking-widest text-white shadow-xl shadow-rose-200 flex items-center justify-center gap-2 transition-all ${
                                loading ? "bg-rose-300" : "bg-rose-600 hover:bg-rose-700 active:scale-95"
                            }`}
                        >
                            <Download size={18} />
                            {loading ? "Creating..." : "Download PDF"}
                        </button>
                    )}
                </PDFDownloadLink>
            </div>
            
            <p className="text-[10px] text-slate-400 mt-6 font-medium flex items-center justify-center gap-1">
                <CheckCircle2 size={10} /> Verified Digital Document
            </p>
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;